import { supabase } from './supabase';

export interface Pet {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed?: string;
  birth_date?: string | null;
  weight?: number;
  color?: string;
  microchip_number?: string;
  registration_number?: string;
  image_url?: string;
  notes?: string;
  guardian_name?: string;
  guardian_phone?: string;
  guardian_email?: string;
  guardian_relationship?: string;
  guardian_address?: string;
  guardian_notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PetCareSchedule {
  id: string;
  pet_id: string;
  type: 'feeding' | 'medication' | 'grooming' | 'exercise' | 'vet_visit' | 'vaccination' | 'other';
  frequency: 'daily' | 'twice_daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
  time?: string;
  days_of_week?: number[];
  custom_frequency?: string;
  description?: string;
  notes?: string;
  is_active: boolean;
  next_due_date?: string;
  created_at: string;
  updated_at: string;
}

export interface PetHealthRecord {
  id: string;
  pet_id: string;
  type: 'vaccination' | 'checkup' | 'surgery' | 'injury' | 'illness' | 'dental' | 'parasite_treatment' | 'other';
  date: string;
  vet_name?: string;
  vet_clinic?: string;
  diagnosis?: string;
  treatment?: string;
  medications?: string[];
  cost?: number;
  next_due_date?: string;
  notes?: string;
  attachments?: string[];
  created_at: string;
  updated_at: string;
}

export interface PetVaccinationRecord {
  id: string;
  pet_id: string;
  vaccine_name: string;
  date_given: string;
  next_due_date?: string;
  vet_name?: string;
  vet_clinic?: string;
  lot_number?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PetVeterinaryAppointment {
  id: string;
  pet_id: string;
  appointment_date: string;
  vet_name?: string;
  vet_clinic?: string;
  reason?: string;
  notes?: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  reminder_sent: boolean;
  created_at: string;
  updated_at: string;
}

export interface PetExpense {
  id: string;
  pet_id: string;
  category: 'food' | 'vet' | 'grooming' | 'toys' | 'equipment' | 'insurance' | 'training' | 'boarding' | 'other';
  amount: number;
  date: string;
  description?: string;
  receipt_url?: string;
  is_recurring: boolean;
  recurring_frequency?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  created_at: string;
  updated_at: string;
}

export interface PetDietNutrition {
  id: string;
  pet_id: string;
  food_name: string;
  food_type?: 'dry' | 'wet' | 'raw' | 'homemade' | 'treats' | 'supplements';
  brand?: string;
  serving_size?: string;
  frequency?: string;
  special_diet: boolean;
  dietary_restrictions?: string[];
  allergies?: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePetData {
  name: string;
  species: string;
  breed?: string;
  birth_date?: string;
  weight?: number;
  color?: string;
  microchip_number?: string;
  registration_number?: string;
  image_url?: string;
  notes?: string;
  guardian_name?: string;
  guardian_phone?: string;
  guardian_email?: string;
  guardian_relationship?: string;
  guardian_address?: string;
  guardian_notes?: string;
}

export interface UpdatePetData extends Partial<CreatePetData> {}

export interface CreatePetCareScheduleData {
  pet_id: string;
  type: PetCareSchedule['type'];
  frequency: PetCareSchedule['frequency'];
  time?: string;
  days_of_week?: number[];
  custom_frequency?: string;
  description?: string;
  notes?: string;
  next_due_date?: string;
}

export interface CreatePetHealthRecordData {
  pet_id: string;
  type: PetHealthRecord['type'];
  date: string;
  vet_name?: string;
  vet_clinic?: string;
  diagnosis?: string;
  treatment?: string;
  medications?: string[];
  cost?: number;
  next_due_date?: string;
  notes?: string;
  attachments?: string[];
}

export interface CreatePetExpenseData {
  pet_id: string;
  category: PetExpense['category'];
  amount: number;
  date: string;
  description?: string;
  receipt_url?: string;
  is_recurring?: boolean;
  recurring_frequency?: PetExpense['recurring_frequency'];
}

class PetsService {
  private async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) {
      throw new Error('User not authenticated');
    }
    return user;
  }

  // Pet CRUD operations
  async getPets(): Promise<Pet[]> {
    try {
      const user = await this.getCurrentUser();
      
      const { data, error } = await supabase
        .from('pets')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pets:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPets:', error);
      throw error;
    }
  }

  async getPet(id: string): Promise<Pet | null> {
    try {
      const user = await this.getCurrentUser();
      
      const { data, error } = await supabase
        .from('pets')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // No rows returned
        }
        console.error('Error fetching pet:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in getPet:', error);
      throw error;
    }
  }

  async createPet(petData: CreatePetData): Promise<Pet> {
    try {
      const user = await this.getCurrentUser();
      
      // Clean the data to remove empty strings that would cause timestamp errors
      const cleanedData = { ...petData };
      
      // Remove empty strings for optional fields
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key as keyof CreatePetData] === '') {
          delete cleanedData[key as keyof CreatePetData];
        }
      });
      
      const { data, error } = await supabase
        .from('pets')
        .insert({
          ...cleanedData,
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating pet:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPet:', error);
      throw error;
    }
  }

  async updatePet(id: string, petData: UpdatePetData): Promise<Pet> {
    try {
      const user = await this.getCurrentUser();
      
      // Clean the data to remove empty strings that would cause timestamp errors
      const cleanedData = { ...petData };
      
      // Remove empty strings for optional fields
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key as keyof UpdatePetData] === '') {
          delete cleanedData[key as keyof UpdatePetData];
        }
      });
      
      const { data, error } = await supabase
        .from('pets')
        .update(cleanedData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePet:', error);
      throw error;
    }
  }

  async deletePet(id: string): Promise<void> {
    try {
      const user = await this.getCurrentUser();
      
      const { error } = await supabase
        .from('pets')
        .update({ is_active: false })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting pet:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePet:', error);
      throw error;
    }
  }

  // Pet Care Schedule operations
  async getPetCareSchedules(petId?: string): Promise<PetCareSchedule[]> {
    try {
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('pet_care_schedules')
        .select(`
          *,
          pets!inner(user_id)
        `)
        .eq('pets.user_id', user.id)
        .eq('is_active', true)
        .order('next_due_date', { ascending: true });

      if (petId) {
        query = query.eq('pet_id', petId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pet care schedules:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetCareSchedules:', error);
      throw error;
    }
  }

  async createPetCareSchedule(scheduleData: CreatePetCareScheduleData): Promise<PetCareSchedule> {
    try {
      const { data, error } = await supabase
        .from('pet_care_schedules')
        .insert(scheduleData)
        .select()
        .single();

      if (error) {
        console.error('Error creating pet care schedule:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPetCareSchedule:', error);
      throw error;
    }
  }

  async updatePetCareSchedule(id: string, scheduleData: Partial<PetCareSchedule>): Promise<PetCareSchedule> {
    try {
      const { data, error } = await supabase
        .from('pet_care_schedules')
        .update(scheduleData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet care schedule:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePetCareSchedule:', error);
      throw error;
    }
  }

  async deletePetCareSchedule(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pet_care_schedules')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        console.error('Error deleting pet care schedule:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetCareSchedule:', error);
      throw error;
    }
  }

  // Pet Health Records operations
  async getPetHealthRecords(petId?: string): Promise<PetHealthRecord[]> {
    try {
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('pet_health_records')
        .select(`
          *,
          pets!inner(user_id)
        `)
        .eq('pets.user_id', user.id)
        .order('date', { ascending: false });

      if (petId) {
        query = query.eq('pet_id', petId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pet health records:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetHealthRecords:', error);
      throw error;
    }
  }

  async createPetHealthRecord(recordData: CreatePetHealthRecordData): Promise<PetHealthRecord> {
    try {
      const { data, error } = await supabase
        .from('pet_health_records')
        .insert(recordData)
        .select()
        .single();

      if (error) {
        console.error('Error creating pet health record:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPetHealthRecord:', error);
      throw error;
    }
  }

  async updatePetHealthRecord(id: string, recordData: Partial<PetHealthRecord>): Promise<PetHealthRecord> {
    try {
      const { data, error } = await supabase
        .from('pet_health_records')
        .update(recordData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet health record:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePetHealthRecord:', error);
      throw error;
    }
  }

  async deletePetHealthRecord(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pet_health_records')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting pet health record:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetHealthRecord:', error);
      throw error;
    }
  }

  // Pet Vaccination Records operations
  async getPetVaccinationRecords(petId?: string): Promise<PetVaccinationRecord[]> {
    try {
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('pet_vaccination_records')
        .select(`
          *,
          pets!inner(user_id)
        `)
        .eq('pets.user_id', user.id)
        .order('date_given', { ascending: false });

      if (petId) {
        query = query.eq('pet_id', petId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pet vaccination records:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetVaccinationRecords:', error);
      throw error;
    }
  }

  async createPetVaccinationRecord(recordData: Omit<PetVaccinationRecord, 'id' | 'created_at' | 'updated_at'>): Promise<PetVaccinationRecord> {
    try {
      const { data, error } = await supabase
        .from('pet_vaccination_records')
        .insert(recordData)
        .select()
        .single();

      if (error) {
        console.error('Error creating pet vaccination record:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPetVaccinationRecord:', error);
      throw error;
    }
  }

  async updatePetVaccinationRecord(id: string, recordData: Partial<PetVaccinationRecord>): Promise<PetVaccinationRecord> {
    try {
      const { data, error } = await supabase
        .from('pet_vaccination_records')
        .update(recordData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet vaccination record:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePetVaccinationRecord:', error);
      throw error;
    }
  }

  async deletePetVaccinationRecord(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pet_vaccination_records')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting pet vaccination record:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetVaccinationRecord:', error);
      throw error;
    }
  }

  // Pet Veterinary Appointments operations
  async getPetVeterinaryAppointments(petId?: string): Promise<PetVeterinaryAppointment[]> {
    try {
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('pet_veterinary_appointments')
        .select(`
          *,
          pets!inner(user_id)
        `)
        .eq('pets.user_id', user.id)
        .order('appointment_date', { ascending: true });

      if (petId) {
        query = query.eq('pet_id', petId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pet veterinary appointments:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetVeterinaryAppointments:', error);
      throw error;
    }
  }

  async createPetVeterinaryAppointment(appointmentData: Omit<PetVeterinaryAppointment, 'id' | 'created_at' | 'updated_at'>): Promise<PetVeterinaryAppointment> {
    try {
      const { data, error } = await supabase
        .from('pet_veterinary_appointments')
        .insert(appointmentData)
        .select()
        .single();

      if (error) {
        console.error('Error creating pet veterinary appointment:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPetVeterinaryAppointment:', error);
      throw error;
    }
  }

  async updatePetVeterinaryAppointment(id: string, appointmentData: Partial<PetVeterinaryAppointment>): Promise<PetVeterinaryAppointment> {
    try {
      const { data, error } = await supabase
        .from('pet_veterinary_appointments')
        .update(appointmentData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet veterinary appointment:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePetVeterinaryAppointment:', error);
      throw error;
    }
  }

  async deletePetVeterinaryAppointment(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pet_veterinary_appointments')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting pet veterinary appointment:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetVeterinaryAppointment:', error);
      throw error;
    }
  }

  // Pet Expenses operations
  async getPetExpenses(petId?: string): Promise<PetExpense[]> {
    try {
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('pet_expenses')
        .select(`
          *,
          pets!inner(user_id)
        `)
        .eq('pets.user_id', user.id)
        .order('date', { ascending: false });

      if (petId) {
        query = query.eq('pet_id', petId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pet expenses:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetExpenses:', error);
      throw error;
    }
  }

  async createPetExpense(expenseData: CreatePetExpenseData): Promise<PetExpense> {
    try {
      const { data, error } = await supabase
        .from('pet_expenses')
        .insert(expenseData)
        .select()
        .single();

      if (error) {
        console.error('Error creating pet expense:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPetExpense:', error);
      throw error;
    }
  }

  async updatePetExpense(id: string, expenseData: Partial<PetExpense>): Promise<PetExpense> {
    try {
      const { data, error } = await supabase
        .from('pet_expenses')
        .update(expenseData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet expense:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePetExpense:', error);
      throw error;
    }
  }

  async deletePetExpense(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pet_expenses')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting pet expense:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetExpense:', error);
      throw error;
    }
  }

  // Pet Diet Nutrition operations
  async getPetDietNutrition(petId?: string): Promise<PetDietNutrition[]> {
    try {
      const user = await this.getCurrentUser();
      
      let query = supabase
        .from('pet_diet_nutrition')
        .select(`
          *,
          pets!inner(user_id)
        `)
        .eq('pets.user_id', user.id)
        .order('created_at', { ascending: false });

      if (petId) {
        query = query.eq('pet_id', petId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pet diet nutrition:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetDietNutrition:', error);
      throw error;
    }
  }

  async createPetDietNutrition(dietData: Omit<PetDietNutrition, 'id' | 'created_at' | 'updated_at'>): Promise<PetDietNutrition> {
    try {
      const { data, error } = await supabase
        .from('pet_diet_nutrition')
        .insert(dietData)
        .select()
        .single();

      if (error) {
        console.error('Error creating pet diet nutrition:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in createPetDietNutrition:', error);
      throw error;
    }
  }

  async updatePetDietNutrition(id: string, dietData: Partial<PetDietNutrition>): Promise<PetDietNutrition> {
    try {
      const { data, error } = await supabase
        .from('pet_diet_nutrition')
        .update(dietData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating pet diet nutrition:', error);
        throw new Error(error.message);
      }
      
      return data;
    } catch (error) {
      console.error('Error in updatePetDietNutrition:', error);
      throw error;
    }
  }

  async deletePetDietNutrition(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pet_diet_nutrition')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting pet diet nutrition:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetDietNutrition:', error);
      throw error;
    }
  }

  // Image upload functionality
  async uploadPetImage(file: File, petId: string): Promise<string> {
    try {
      const user = await this.getCurrentUser();
      
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${petId}/pet-image-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('pet-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading image:', error);
        throw new Error(error.message);
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('pet-photos')
        .getPublicUrl(fileName);

      console.log('Uploaded image URL:', urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error in uploadPetImage:', error);
      throw error;
    }
  }

  async deletePetImage(imageUrl: string): Promise<void> {
    try {
      // Extract the file path from the URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts.slice(-2).join('/'); // Get the last two parts (user_id/filename)
      
      const { error } = await supabase.storage
        .from('pet-photos')
        .remove([fileName]);

      if (error) {
        console.error('Error deleting image:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in deletePetImage:', error);
      throw error;
    }
  }

  // Utility functions
  async getUpcomingPetCareTasks(daysAhead: number = 7): Promise<any[]> {
    try {
      const user = await this.getCurrentUser();
      
      const { data, error } = await supabase
        .rpc('get_upcoming_pet_care_tasks', {
          user_uuid: user.id,
          days_ahead: daysAhead
        });

      if (error) {
        // Handle missing table/function gracefully
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('⚠️ Pet care schedules table or function does not exist. Returning empty array.');
          return [];
        }
        console.error('Error fetching upcoming pet care tasks:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error: any) {
      // Handle missing table/function gracefully
      if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
        console.warn('⚠️ Pet care schedules table or function does not exist. Returning empty array.');
        return [];
      }
      console.error('Error in getUpcomingPetCareTasks:', error);
      throw error;
    }
  }

  async getPetsWithAge(): Promise<any[]> {
    try {
      const user = await this.getCurrentUser();
      
      const { data, error } = await supabase
        .from('pets_with_age')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pets with age:', error);
        throw new Error(error.message);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getPetsWithAge:', error);
      throw error;
    }
  }
}

export const petsService = new PetsService(); 